"use client";

import { useTranslations } from "use-intl";

import { FormatIcon } from "@/components/format-icon";
import { Link, type LinkTarget } from "@/components/link";
import type { DealOfferFormatOption } from "@/components/marketplace/deal-offers-types";
import { DealOfferFormatTabDetails } from "@/components/marketplace/DealOfferFormatTabDetails";
import { cn } from "@/lib/utils";

export function DealOfferFormatTab({
  active,
  formatLinkTarget,
  onSelect,
  option,
}: {
  active: boolean;
  formatLinkTarget?: (format: DealOfferFormatOption["format"]) => LinkTarget;
  onSelect: (format: DealOfferFormatOption["format"]) => void;
  option: DealOfferFormatOption;
}) {
  const t = useTranslations("shared.affiliates");
  const tFormats = useTranslations("shared.releaseFilters");
  const label = tFormats.has(option.format) ? tFormats(option.format) : option.format;
  const outOfStock = option.availability === "out_of_stock";
  const linkTarget = formatLinkTarget?.(option.format);
  const accessibleLabel = outOfStock ? `${label} · ${t("outOfStock")}` : label;
  const className = cn(
    "flex min-h-11 min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
    active
      ? "border-primary/60 bg-primary/10 text-foreground"
      : "border-border bg-secondary/40 text-foreground hover:border-primary/60 hover:bg-secondary",
  );
  const content = (
    <>
      <FormatIcon
        format={option.format}
        size={18}
        className={cn("shrink-0", active ? "text-primary" : "text-muted-foreground")}
      />
      <span className="font-bold">{label}</span>
      <DealOfferFormatTabDetails
        active={active}
        offerCount={option.offerCount}
        outOfStock={outOfStock}
        price={outOfStock ? null : option.price}
        currency={option.currency}
      />
    </>
  );

  if (linkTarget) {
    return (
      <Link
        {...linkTarget}
        rel="nofollow"
        aria-label={accessibleLabel}
        aria-current={active ? "true" : undefined}
        onClick={() => onSelect(option.format)}
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={accessibleLabel}
      aria-pressed={active}
      onClick={() => onSelect(option.format)}
      className={className}
    >
      {content}
    </button>
  );
}
